<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center">
    <div class="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
      <div class="text-center">
        <div class="mb-6">
          <div class="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg v-if="!hasError" class="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <svg v-else class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Panel de Administración</h2>
          <p class="text-gray-600">{{ statusMessage }}</p>
        </div>
        
        <div class="space-y-3">
          <div v-if="!hasError" class="w-full bg-gray-200 rounded-full h-2">
            <div class="bg-blue-600 h-2 rounded-full transition-all duration-1000 ease-out" :style="`width: ${progress}%`"></div>
          </div>
          <p class="text-sm" :class="hasError ? 'text-red-500' : 'text-gray-500'">
            {{ detailMessage }}
          </p>
          
          <div v-if="hasError" class="mt-4">
            <button 
              @click="retry" 
              class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              Reintentar
            </button>
          </div>
          <p>Verificando credenciales de administrador</p>
          <p>Conectando con servidor de autenticación</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const statusMessage = ref('Verificando sesión...')
const detailMessage = ref('Inicializando sistema de autenticación')
const progress = ref(0)
const hasError = ref(false)

// Función para simular progreso de carga
const updateProgress = (value: number, status: string, detail: string) => {
  progress.value = value
  statusMessage.value = status
  detailMessage.value = detail
}

const retry = async () => {
  hasError.value = false
  progress.value = 0
  await initializeAuth()
}

const initializeAuth = async () => {
  try {
    console.log('🔄 KeycloakInit: Inicializando autenticación...')
    
    updateProgress(20, 'Verificando sesión...', 'Conectando con servidor de autenticación')
    
    // Pequeña pausa para mostrar el progreso
    await new Promise(resolve => setTimeout(resolve, 500))
    
    updateProgress(50, 'Verificando sesión...', 'Validando credenciales')
    
    // Inicializar el store de autenticación
    await authStore.initialize()
    
    updateProgress(80, 'Verificando permisos...', 'Comprobando rol de administrador')
    
    await new Promise(resolve => setTimeout(resolve, 300))
    
    // Verificar si el usuario está autenticado y tiene permisos de admin
    if (authStore.isAuthenticated && authStore.isAdmin) {
      console.log('✅ KeycloakInit: Usuario autenticado con permisos de admin')
      updateProgress(100, 'Acceso autorizado', 'Redirigiendo al panel de administración')
      
      await new Promise(resolve => setTimeout(resolve, 500))
      router.push('/dashboard')
    } else {
      console.log('ℹ️ KeycloakInit: Usuario no autenticado, redirigiendo al login')
      updateProgress(100, 'Sesión no encontrada', 'Redirigiendo al login')
      
      await new Promise(resolve => setTimeout(resolve, 800))
      router.push('/login')
    }
  } catch (error) {
    console.error('❌ KeycloakInit: Error durante inicialización:', error)
    hasError.value = true
    statusMessage.value = 'Error de conexión'
    detailMessage.value = 'No se pudo conectar con el servidor de autenticación'
  }
}

onMounted(async () => {
  await initializeAuth()
})
</script>
